import { useCallback, useEffect } from 'react';

type KeyboardEventCallback = (event: KeyboardEvent) => Promise<void> | void;

/**
 * Hook used to track keyboard event, prefers keydown event
 * @param targetKey
 * @param callback
 */
export function useKeyboardEvent(
  targetKey: string | string[],
  callback: KeyboardEventCallback,
  options?: {
    ref?: React.RefObject<any>;
    key?: 'keydown' | 'keyup';
  },
): void {
  const handleKeyDown = useCallback(
    async (event: KeyboardEvent) => {
      const keys = Array.isArray(targetKey) ? targetKey : [targetKey];
      if (keys.includes(event.key)) {
        if (callback instanceof Promise) {
          await callback(event);
        } else {
          callback(event);
        }
      }
    },
    [targetKey, callback],
  );

  useEffect(() => {
    const tmp = options?.ref?.current ? options.ref.current : window;
    tmp.addEventListener(options?.key || 'keyup', handleKeyDown);
    return () => {
      tmp.removeEventListener(options?.key || 'keyup', handleKeyDown);
    };
  }, [handleKeyDown, options?.key, options?.ref]);
}

export default useKeyboardEvent;
